import React, { useState, useEffect } from 'react';
import { Search, Check, RefreshCw, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getAccessToken } from '../services/auth';

const BC_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/bc-proxy'
  : '/.netlify/functions/bc-proxy';

const WOS_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/wos-proxy'
  : '/.netlify/functions/wos-proxy';

async function bcApi(method, path, body) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(BC_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ method, path, body }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.title || data?.error || 'BC API error');
  return data;
}

function wosGet(endpoint) {
  return fetch(WOS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  }).then(r => r.json());
}

async function fetchAllPages(basePath) {
  let page = 1;
  let all = [];
  while (true) {
    const sep = basePath.includes('?') ? '&' : '?';
    const res = await bcApi('GET', `${basePath}${sep}page=${page}&limit=250`);
    all = all.concat(res?.data || []);
    if (!res?.meta?.pagination || page >= res.meta.pagination.total_pages) break;
    page++;
  }
  return all;
}

function extractWosKeys(bcSku) {
  if (!bcSku) return [];
  const match = bcSku.toUpperCase().match(/(?:^|[-])((CP\d+[A-Z]?))/);
  if (!match) return [];
  const raw = match[1];
  const keys = [raw];
  const stripped = raw.replace(/^(CP)0+(\d+[A-Z]?)$/, '$1$2');
  if (stripped !== raw) {
    keys.push(stripped);
    const oneZero = raw.replace(/^(CP)0{2,}(\d+[A-Z]?)$/, '$10$2');
    if (oneZero !== raw && oneZero !== stripped) keys.push(oneZero);
  }
  const noTrailLetter = raw.replace(/([A-Z])$/, '');
  if (noTrailLetter !== raw) keys.push(noTrailLetter);
  return keys;
}

// Auto-detect multiplier from SKU against WOS unit cost
function detectMultiplier(sku) {
  const s = sku.toUpperCase();
  const lbMatch = s.match(/(\d+)LBS?(?:-|$)/);
  if (lbMatch) return parseInt(lbMatch[1]);
  if (/-2DZ-|-2DZ$/.test(s)) return 24;
  if (/-1DZ-|-1DZ$/.test(s)) return 12;
  if (/-DZ-|-DZ$/.test(s)) return 12;
  if (/-HD-|-HD$/.test(s)) return 6;
  return 1;
}

export default function CostMapping() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [showMappedOnly, setShowMappedOnly] = useState(false);

  const [wosItems, setWosItems] = useState([]);
  const [bcSkusByWos, setBcSkusByWos] = useState({});
  // Edits: { `wosKey|bcSku`: multiplier (number or string) }
  const [savedMultipliers, setSavedMultipliers] = useState({});
  const [multipliers, setMultipliers] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [wosRes, savedRes, overridesRes] = await Promise.all([
        wosGet('/api/InventoryApi/InventoryReport'),
        neonQuery('sku_mappings_all'),
        neonQuery('wos_overrides_all'),
      ]);

      const wosRaw = Array.isArray(wosRes) ? wosRes : wosRes?.inventories ?? [];
      const wosMap = {};
      const wosList = [];
      wosRaw.forEach(r => {
        if (!r.item || r.inventoryCost == null) return;
        const key = r.item.toUpperCase();
        wosMap[key] = true;
        wosList.push({ key, desc: r.itemDescription || '', cost: r.inventoryCost });
      });
      wosList.sort((a, b) => a.key.localeCompare(b.key));
      setWosItems(wosList);

      // SKU overrides: bc_sku → forced wos_key
      const overrides = {};
      (overridesRes || []).forEach(r => { overrides[r.bc_sku.toUpperCase()] = r.wos_key.toUpperCase(); });

      // Saved multipliers
      const saved = {};
      (savedRes || []).forEach(r => {
        saved[`${r.wos_key}|${r.bc_sku}`] = parseFloat(r.multiplier);
      });
      setSavedMultipliers(saved);

      // BC products
      let allProducts = [];
      let page = 1;
      while (true) {
        const res = await bcApi('GET', `/v3/catalog/products?include=variants&include_fields=name,sku,is_visible&is_visible=true&page=${page}&limit=250`);
        allProducts = allProducts.concat(res?.data || []);
        if (!res?.meta?.pagination || page >= res.meta.pagination.total_pages) break;
        page++;
      }

      const byWos = {};
      for (const p of allProducts) {
        const variants = p.variants?.length > 0 ? p.variants : [{ sku: p.sku, purchasing_disabled: false }];
        for (const v of variants) {
          if (v.purchasing_disabled) continue;
          const sku = v.sku || p.sku || '';
          if (!sku) continue;

          // Check for override first, then auto-detect from SKU
          let matchedKey = overrides[sku.toUpperCase()] || null;
          if (!matchedKey) {
            const keys = extractWosKeys(sku);
            for (const k of keys) {
              if (wosMap[k]) { matchedKey = k; break; }
            }
          }
          if (!matchedKey || !wosMap[matchedKey]) continue;

          if (!byWos[matchedKey]) byWos[matchedKey] = [];
          byWos[matchedKey].push({
            sku,
            productName: p.name,
            variant: v.option_values?.map(o => o.label).join(', ') || '',
            autoMult: detectMultiplier(sku),
            overridden: !!overrides[sku.toUpperCase()],
          });
        }
      }

      Object.values(byWos).forEach(arr => arr.sort((a, b) => a.autoMult - b.autoMult));
      setBcSkusByWos(byWos);

      // Initialize multipliers: use saved values only — unsaved items start empty
      // so you can see what's been reviewed vs not
      const mults = { ...saved };
      setMultipliers(mults);

      // Auto-expand saved items
      const autoExpand = new Set();
      Object.keys(saved).forEach(k => autoExpand.add(k.split('|')[0]));
      setExpanded(autoExpand);
    } catch (e) {
      console.error('Failed to load cost mapping data:', e);
    }
    setLoading(false);
  };

  const eKey = (wosKey, bcSku) => `${wosKey}|${bcSku}`;

  const setMult = (wosKey, bcSku, value) => {
    setMultipliers(prev => ({ ...prev, [eKey(wosKey, bcSku)]: value === '' ? '' : value }));
  };

  const toggleExpand = (wosKey) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(wosKey)) next.delete(wosKey);
      else next.add(wosKey);
      return next;
    });
  };

  // Accept auto-detected multipliers (relative to base) for all SKUs in a WOS group
  const acceptAll = (wosKey) => {
    const skus = bcSkusByWos[wosKey] || [];
    const baseMult = skus[0]?.autoMult || 1;
    setMultipliers(prev => {
      const next = { ...prev };
      skus.forEach(s => { next[eKey(wosKey, s.sku)] = s.autoMult / baseMult; });
      return next;
    });
  };

  const hasChanges = JSON.stringify(multipliers) !== JSON.stringify(savedMultipliers);

  const saveChanges = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const mappings = [];
      for (const [key, mult] of Object.entries(multipliers)) {
        const [wosKey, bcSku] = key.split('|');
        if (!wosKey || !bcSku) continue;
        const m = parseFloat(mult);
        if (isNaN(m)) continue;
        mappings.push({ wos_key: wosKey, bc_sku: bcSku, is_base: m === 1, multiplier: m });
      }

      if (mappings.length === 0) {
        setSaveResult({ type: 'info', message: 'No mappings to save' });
        setSaving(false);
        return;
      }

      await neonQuery('sku_mappings_save', { mappings });

      // Update cp_costs
      const cpCostItems = [];
      for (const m of mappings) {
        const wosItem = wosItems.find(w => w.key === m.wos_key);
        if (!wosItem) continue;
        cpCostItems.push({ sku: m.bc_sku, cost: parseFloat((wosItem.cost * m.multiplier).toFixed(2)) });
      }

      if (cpCostItems.length > 0) {
        await neonQuery('cp_costs_bulk_mapped', { items: cpCostItems });
      }

      setSavedMultipliers({ ...multipliers });
      setSaveResult({ type: 'success', message: `${mappings.length} mapping${mappings.length !== 1 ? 's' : ''} saved, ${cpCostItems.length} CP costs updated` });
    } catch (e) {
      setSaveResult({ type: 'error', message: e.message });
    }
    setSaving(false);
  };

  // Filter
  const filteredWos = wosItems.filter(w => {
    const skus = bcSkusByWos[w.key] || [];
    if (skus.length === 0) return false;
    if (showMappedOnly) {
      const hasSaved = skus.some(s => savedMultipliers[eKey(w.key, s.sku)] !== undefined);
      if (!hasSaved) return false;
    }
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    if (w.key.toLowerCase().includes(term) || w.desc.toLowerCase().includes(term)) return true;
    return skus.some(s => s.sku.toLowerCase().includes(term) || s.productName.toLowerCase().includes(term));
  });

  const totalMapped = Object.keys(savedMultipliers).length;
  const totalSkus = Object.values(bcSkusByWos).reduce((s, arr) => s + arr.length, 0);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Cost Mapping</h1>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: '4px 0 0' }}>
            Map WOS unit costs to BigCommerce SKUs — WOS cost x multiplier = CP Cost
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {hasChanges && (
            <button className="btn btn-primary" onClick={saveChanges} disabled={saving} style={{ fontSize: 13 }}>
              {saving ? <><RefreshCw size={14} className="spin" /> Saving...</> : <><Save size={14} /> Save Mappings</>}
            </button>
          )}
        </div>
      </div>

      {saveResult && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: saveResult.type === 'success' ? '#e6f9e6' : saveResult.type === 'error' ? '#fde8e8' : '#fff3e0',
          color: saveResult.type === 'success' ? '#1a7a1a' : saveResult.type === 'error' ? '#c53030' : '#b45309',
        }}>
          {saveResult.message}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
            <input
              className="form-input"
              placeholder="Search WOS item, description, or BC SKU..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 30, fontSize: 13 }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={showMappedOnly} onChange={e => setShowMappedOnly(e.target.checked)} style={{ accentColor: 'var(--orange)' }} />
            Saved only
          </label>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            {totalMapped} of {totalSkus} SKUs mapped
          </span>
          <button className="btn btn-outline" onClick={() => setExpanded(new Set(filteredWos.map(w => w.key)))} style={{ fontSize: 12 }}>
            Expand All
          </button>
          <button className="btn btn-outline" onClick={() => setExpanded(new Set())} style={{ fontSize: 12 }}>
            Collapse All
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
          <RefreshCw size={20} className="spin" style={{ marginBottom: 8 }} />
          <div>Loading WOS items and BC products...</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filteredWos.map(w => {
            const isOpen = expanded.has(w.key);
            const bcSkus = bcSkusByWos[w.key] || [];
            const savedCount = bcSkus.filter(s => savedMultipliers[eKey(w.key, s.sku)] !== undefined).length;

            return (
              <div key={w.key} className="card" style={{ overflow: 'hidden' }}>
                {/* WOS item header */}
                <div
                  onClick={() => toggleExpand(w.key)}
                  style={{
                    padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
                    cursor: 'pointer', background: isOpen ? 'var(--gray-50)' : 'white',
                    borderBottom: isOpen ? '1px solid var(--gray-200)' : 'none',
                  }}
                >
                  {isOpen ? <ChevronDown size={14} color="var(--gray-500)" /> : <ChevronRight size={14} color="var(--gray-500)" />}
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--navy)', fontSize: 13, minWidth: 80 }}>
                    {w.key}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--gray-700)', flex: 1 }}>{w.desc}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', minWidth: 80, textAlign: 'right' }}>
                    ${w.cost.toFixed(2)}/unit
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--gray-400)', minWidth: 60, textAlign: 'right' }}>
                    {bcSkus.length} SKU{bcSkus.length !== 1 ? 's' : ''}
                  </span>
                  {savedCount === bcSkus.length ? (
                    <Check size={14} color="#22c55e" style={{ minWidth: 20 }} />
                  ) : savedCount > 0 ? (
                    <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600, minWidth: 20 }}>
                      {savedCount}/{bcSkus.length}
                    </span>
                  ) : (
                    <span style={{ minWidth: 20 }} />
                  )}
                </div>

                {/* BC SKU rows */}
                {isOpen && (
                  <div>
                    <div style={{ padding: '6px 16px', display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid var(--gray-100)' }}>
                      <button
                        className="btn btn-ghost"
                        onClick={(e) => { e.stopPropagation(); acceptAll(w.key); }}
                        style={{ fontSize: 11, padding: '3px 10px' }}
                      >
                        Accept Auto-Detected
                      </button>
                    </div>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                          <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--gray-500)' }}>BC SKU</th>
                          <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--gray-500)' }}>Product / Variant</th>
                          <th style={{ width: 70, textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)' }}>Auto</th>
                          <th style={{ width: 90, textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)' }}>Multiplier</th>
                          <th style={{ width: 110, textAlign: 'right', padding: '6px 12px', fontWeight: 600, color: 'var(--gray-500)' }}>CP Cost</th>
                          <th style={{ width: 50, textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)' }}>Saved</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bcSkus.map(s => {
                          const k = eKey(w.key, s.sku);
                          const mult = multipliers[k] !== undefined ? multipliers[k] : '';
                          const parsedMult = parseFloat(mult);
                          const calcCost = !isNaN(parsedMult) ? (w.cost * parsedMult).toFixed(2) : null;
                          const isSaved = savedMultipliers[k] !== undefined;
                          const isChanged = isSaved ? savedMultipliers[k] !== parseFloat(mult) : mult !== '';

                          return (
                            <tr key={s.sku} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                              <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 11, color: 'var(--gray-600)' }}>
                                {s.sku}
                                {s.overridden && <span style={{ marginLeft: 6, fontSize: 9, color: 'var(--orange)', fontWeight: 700, fontFamily: 'sans-serif' }}>OVERRIDE</span>}
                              </td>
                              <td style={{ padding: '6px 12px', color: 'var(--gray-700)' }}>
                                {s.productName}{s.variant ? ` (${s.variant})` : ''}
                              </td>
                              <td style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--gray-400)', fontSize: 11 }}>
                                {(() => {
                                  const baseMult = bcSkus[0]?.autoMult || 1;
                                  const rel = s.autoMult / baseMult;
                                  return `${rel % 1 === 0 ? rel : rel.toFixed(2)}x`;
                                })()}
                              </td>
                              <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  value={mult}
                                  onChange={e => setMult(w.key, s.sku, e.target.value)}
                                  style={{
                                    width: 60, textAlign: 'center', padding: '3px 6px',
                                    border: isChanged ? '2px solid var(--orange)' : '1px solid var(--gray-200)',
                                    borderRadius: 4, fontSize: 12,
                                    background: isChanged ? '#fff8f5' : 'white',
                                  }}
                                />
                              </td>
                              <td style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 600, color: calcCost ? 'var(--navy)' : 'var(--gray-300)', fontSize: 13 }}>
                                {calcCost ? `$${calcCost}` : '--'}
                              </td>
                              <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                {isSaved && <Check size={12} color="#22c55e" />}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
